import React, { useContext, useRef } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ColWithGap, Row, RowForTagsAndBadges } from '@components/styles';
import DistributionValidTillTag from '@components/framework/tags/DistributionValidTillTag.tsx';
import ExpectedPriceTag from '@components/framework/tags/ExpectedPriceTag.tsx';
import RequestedPriceTag from '@components/framework/tags/RequestedPriceTag.tsx';
import SentBadge from '@components/ringil3/Badges/SentBadge';
import SpotQuoteTable, {
  SpotQuoteTableHandle,
} from '@components/ringil3/Features/Expedition/QuoteTable/SpotQuoteTable';
import {
  DistributionWithQuotesV4Response,
  ExpeditionDetailV4OutDTO,
  ExpeditionStatusEnum,
  FeaturesEnum,
} from '@api/ringil-api.v2';
import RowButton from '@components/RingilComponents/RowButton.tsx';
import SvgLkw from '@components/ringil3/icons/heading/Lkw';
import { useCreateAddMoreCarriers } from '@components/ringil3/Features/Expedition/Sidebars/AddMoreCarriersSidebar.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import {
  apiUtcFromDayjs,
  dayjsFromApiUtc,
  stringFromTimeResponse,
} from '@utils/dateWithTz.tsx';
import { ApiContext } from '@api/api.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import LadenFactorTag from '@components/framework/tags/LadenFactorTag.tsx';
import RequestedCarriersTag from '@components/framework/tags/RequestedCarriersTag.tsx';
import styled from 'styled-components';
import { isNullOrUndefined } from '@utils/utils.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';
import { SecuredFlag } from '@components/security/types';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import ContractedCarriersSection from '@components/ringil3/Features/Contracted/ContractedCarriersSection.tsx';
import { fromContractedCarrierOutDTO, fromPriceTableDTO } from '@components/ringil3/Features/Contracted/types.ts';
import { isContracted } from '@utils/distributionType.ts';

interface ComponentProps {
  expedition: ExpeditionDetailV4OutDTO;
  distribution: DistributionWithQuotesV4Response;
  onReload: () => void;
  heading?: string;
  showPrices: SecuredFlag;
  showMessages: SecuredFlag;
  editMessages: SecuredFlag;
  editTransportationFlag: SecuredFlag;
  activeDistribution: boolean;
  headerActions?: React.ReactNode;
  /** Suppress the own headline when a surrounding SectionPanel already provides one (e.g. history). */
  hideHeader?: boolean;
}

const TabExpeditionQuotes: React.FC<ComponentProps> = ({ expedition, distribution, onReload, heading, showPrices: showPricesFlag, editMessages, showMessages, editTransportationFlag, activeDistribution, headerActions, hideHeader = false }) => {
  const showPrices = showPricesFlag.granted;
  const { tr } = useTranslationLgs();
  const { changeExpeditionDistributionDate } = useContext(ApiContext);
  const { loggedUser } = useContext(AuthenticatedUserContext);

  const { AddMoreCarriers, setVisible: setVisibleAddMoreCarriers } = useCreateAddMoreCarriers();

  const transportationEditableFlag = useSecuredFlag(`quotes-transportation-editable-${distribution.distributionId}`, {
    kind: 'visibility',
    description: 'Distribution editable for transportation actions (active + no assigned carrier)',
    state: [
      { label: 'active distribution', satisfied: activeDistribution, description: 'Only quotes in the active distribution can be picked or rejected', byDesign: true },
      { label: 'no assigned carrier', satisfied: isNullOrUndefined(expedition.assignedCarrierId), description: 'Not available once a carrier is assigned to the shipment', byDesign: true },
    ],
  });
  const canEdit = editTransportationFlag.granted && transportationEditableFlag.granted;

  const editableTags = canEdit && distribution.isActive;

  const freeManipulationUnits = React.useMemo(() => {
    if (!expedition?.handlingUnitCount || !expedition.ladenFactorExpeditions?.length) {
      return expedition?.handlingUnitCount || 0;
    }

    const activeLadenExpeditions = expedition.ladenFactorExpeditions.filter(
      ladenExpedition => ladenExpedition.expeditionStatus !== ExpeditionStatusEnum.CANCELED
    );

    const ladenUnits = activeLadenExpeditions.reduce((sum, ladenExpedition) => {
      return sum + (ladenExpedition.manipulationUnits || 0);
    }, 0);

    return Math.max(0, expedition.handlingUnitCount - ladenUnits);
  }, [expedition?.handlingUnitCount, expedition?.ladenFactorExpeditions]);

  const ladenFactorFlag = useSecuredFlag('quotes-laden-factor', {
    kind: 'visibility',
    description: 'Laden factor tag (distribution)',
    features: [FeaturesEnum.LADEN_FACTOR],
  });

  const contracted = isContracted(distribution?.distributionType);
  const contractedTable = React.useMemo(
    () => (contracted && distribution?.contractedPriceTable ? fromPriceTableDTO(distribution.contractedPriceTable) : null),
    [contracted, distribution?.contractedPriceTable],
  );
  const contractedCarriers = React.useMemo(
    () => (contractedTable ? (distribution?.contractedCarriers ?? []).map(c => fromContractedCarrierOutDTO(c, contractedTable)) : []),
    [contractedTable, distribution?.contractedCarriers],
  );

  // CONTRACTED carriers and prices are fixed once sent — the backend refuses more carriers.
  const addNewCarrierEnabled =
    !contracted && ((expedition.ladenFactor > 0 && freeManipulationUnits > 0) || expedition.ladenFactor === 0 || isNullOrUndefined(expedition.ladenFactor));

  const spotQuoteTableRef = useRef<SpotQuoteTableHandle>(null);

  return (
    <>
      <div className="py-6">
        <ColWithGap>
          {!hideHeader && (
            <COHeadline variant={'h2'} title={heading ? heading : tr(`ExpeditionDetail.assignedCarriers`, `Selected carriers`)} icon={<SvgLkw />} actions={headerActions}></COHeadline>
          )}
          <RowForTagsAndBadges>
            <Row>
              <RequestedCarriersTag count={distribution?.quotesCount} dataTestId={'carrierSelection-nbrCarriers'} />
              <DistributionValidTillTag
                onChanged={(v, utcDateTime, timezoneId) => {
                  console.log('Changing distribution date to:', v, utcDateTime.format(), timezoneId);
                  changeExpeditionDistributionDate(
                    expedition.id,
                    {
                      localDateTime: stringFromTimeResponse(v),
                      timeZone: v.timeZone,
                      utcDateTime: apiUtcFromDayjs(utcDateTime),
                      timezoneId: timezoneId,
                    },
                    () => onReload(),
                  );
                }}
                readonly={!canEdit}
                dateTimeResponse={distribution?.validTill}
                utcDateTime={dayjsFromApiUtc(distribution?.utcEnd)}
                userTimezoneId={loggedUser?.defaultTimezoneId}
                dataTestId={'carrierSelection-demandValidTill'}
              />
              {distribution?.distributionType === 'SPOT' && (
                <ExpectedPriceTag
                  priceDto={distribution?.priceDTO}
                  expeditionId={expedition.id}
                  onReload={onReload}
                  readOnly={!editableTags}
                  showPrices={showPrices}
                  dataTestId={'carrierSelection-price'}
                />
              )}
              {distribution?.distributionType === 'FIXED_PRICE' && (
                <RequestedPriceTag
                  priceDto={distribution?.priceDTO}
                  expeditionId={expedition.id}
                  onReload={onReload}
                  showPrices={showPrices}
                  readOnly={!editableTags}
                  dataTestId={'carrierSelection-price'}
                />
              )}
              <SecuredArea
                id="quotes-laden-factor"
                label="LadenFactorTag" description="Laden factor of the distribution"
                visible={[ladenFactorFlag, { type: 'data', label: 'distribution.ladenFactor', satisfied: !!distribution.ladenFactor }]}
              >
                <LadenFactorTag ladenFactor={distribution.ladenFactor} />
              </SecuredArea>
              {contracted && (
                <ButtonTag
                  label={tr('TabExpeditionQuotes.distributionType', 'Způsob výběru')}
                  value={tr('CarrierDistributionSelector.distributionTypeContracted', 'Smluvní dopravce')}
                  readonly={true}
                  dataTestId={'carrierSelection-distributionType'}
                />
              )}
            </Row>
            <BadgeRow>
              <SentBadge fullName={distribution?.createdByName} sentAt={distribution?.createdAt} timezoneId={loggedUser?.defaultTimezoneId} dataTestId='carrierSelection-sentAt'/>
            </BadgeRow>
          </RowForTagsAndBadges>
          {contracted && showPrices && contractedCarriers.length > 0 && contractedTable && (
            <ContractedCarriersSection
              mode={'expedition'}
              table={contractedTable}
              carriers={contractedCarriers}
              onChange={() => {}}
              readOnly
              pricedLevelName={distribution.contractedPriceLevelName}
              shipmentLadenFactor={distribution.ladenFactor || null}
            />
          )}
          {distribution?.quotesCount > 0 && (
            <SpotQuoteTable
              ref={spotQuoteTableRef}
              onChanged={() => onReload()}
              expeditionId={expedition.id}
              distributionId={distribution.distributionId}
              companyId={expedition.customerId}
              showPrices={showPricesFlag}
              showMessages={showMessages}
              editMessages={editMessages}
              editTransportationFlag={editTransportationFlag}
              transportationEditableFlag={transportationEditableFlag}
              ladenFactor={expedition.ladenFactor}
              freeManipulationUnits={freeManipulationUnits}
              expeditionStatus={expedition.expeditionStatus}
              distributionUtcEnd={distribution?.utcEnd}
              activeDistribution={activeDistribution}
            />
          )}

          {expedition.expeditionStatus === ExpeditionStatusEnum.ASSIGNING && canEdit && addNewCarrierEnabled && (
            <RowButton
              title={tr(`ExpeditionDetail.addAnotherCarrier`, `Add another carrier`)}
              onClick={() => setVisibleAddMoreCarriers(true)}
            />
          )}
        </ColWithGap>
        {canEdit && addNewCarrierEnabled && (
          <AddMoreCarriers
            onContinue={() => {
              onReload();
              spotQuoteTableRef.current?.reloadData();
            }}
            aiCarrierAutoSelectionDto={
              {
                loadingLocationId: expedition?.locations[0]?.id,
                unloadingLocationId: expedition?.locations[expedition.locations.length - 1]?.id,
                loadingCity: expedition?.locations[0]?.city,
                loadingCountry: expedition?.locations[0]?.country,
                unloadingCity: expedition?.locations[expedition.locations.length - 1]?.city,
                unloadingCountry: expedition?.locations[expedition.locations.length - 1]?.country,
                weight: expedition?.locations[0]?.loadedOrders?.reduce((sum, order) => sum + (order.items?.reduce?.((itemSum, item) => itemSum + (item.weight || 0), 0) || 0), 0) || 0,
                countPallets: expedition?.locations[0]?.loadedOrders?.reduce((sum, order) => sum + (order.items?.reduce?.((itemSum, item) => itemSum + (item.amount || 0), 0) || 0), 0) || 0,
                distance: expedition.distance,
                cargoAmount: expedition?.locations[0]?.loadedOrders?.reduce((sum, order) => sum + (order.orderAmount || 0), 0) || 0,
              }
            }
            expedition={expedition}
          />
        )}
      </div>
    </>
  );
};

const BadgeRow = styled.div`
  display: flex;
  gap: var(--ringil-cyan-gap);
`;

export default TabExpeditionQuotes;
